import * as React from "react";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Avatar, Chip, IconButton, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  colors,
  spacing,
  typography,
} from "../../../../../../../../../assets/styles/tokens";
import CloseIcon from "@mui/icons-material/Close";
import { Stack } from "@mui/system";
import { Divider, List, ListItem } from "@mui/material";
import robot from "../../../../../../../../../assets/robot.svg";

const Puller = styled("div")(({ theme }) => ({
  width: spacing.Spacing["4xl"].$value,
  height: spacing.Spacing.xs.$value,
  backgroundColor: colors.GrayLightMode[300].$value,
  borderRadius: spacing.Radius.xs.$value,
  position: "absolute",
  top: spacing.Spacing.xl.$value,
  left: "calc(50% - 15px)",
  ...theme.applyStyles("dark", {
    backgroundColor: colors.GrayLightMode[300].$value,
  }),
}));

interface LastRunListProps {
  title: string;
  brandId: string;
  runDate: string;
  runRange: string;
  runBy: string;
  status: string;
}

const BrandsSearchItemsCardLastRun = ({
  title,
  brandId,
  runDate,
  runRange,
  runBy,
  status,
}: LastRunListProps) => {
  const [open, setOpen] = React.useState(false);

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
  };

  const isSystem = runBy?.toLowerCase() === "system";
  const firstLetter = runBy?.charAt(0).toUpperCase();

  const LastRunList = (
    <Box role="LastRun">
      <Puller />
      <Stack justifyContent={"end"} direction="row" p={1}>
        <IconButton
          aria-label="Logout"
          onClick={toggleDrawer(false)}
          sx={{
            width: spacing.Spacing["3xl"].$value,
            height: spacing.Spacing["3xl"].$value,
            color: colors.GrayLightMode[700].$value,
          }}
        >
          <CloseIcon />
        </IconButton>
      </Stack>

      <Typography
        variant="body1"
        sx={{
          fontSize: typography.Size.Text.md.$value,
          color: colors.GrayLightMode[900].$value,
        }}
      >
        Data Collection Last Run
      </Typography>

      <Stack>
        <List sx={{ color: colors.GrayLightMode[900].$value }}>
          <ListItem
            sx={{
              width: "100%",
              display: "block",
              p: spacing.Spacing.sm.$value,
            }}
          >
            <Typography
              sx={{
                fontSize: typography.Size.Text.xs.$value,
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              Brand Name
              <Box sx={{ fontWeight: typography.Font_weight.Semibold.$value }}>
                {title}
              </Box>
            </Typography>
          </ListItem>
          <Divider />
          <ListItem
            sx={{
              width: "100%",
              display: "block",
              p: spacing.Spacing.sm.$value,
            }}
          >
            <Typography
              sx={{
                fontSize: typography.Size.Text.xs.$value,
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              Brand ID
              <Box sx={{ fontWeight: typography.Font_weight.Semibold.$value }}>
                {brandId}
              </Box>
            </Typography>
          </ListItem>
          <Divider />
          <ListItem
            sx={{
              width: "100%",
              display: "block",
              p: spacing.Spacing.sm.$value,
            }}
          >
            <Typography
              sx={{
                fontSize: typography.Size.Text.xs.$value,
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              Run Date
              <Box sx={{ fontWeight: typography.Font_weight.Semibold.$value }}>
                {runDate}
              </Box>
            </Typography>
          </ListItem>
          <Divider />
          <ListItem
            sx={{
              width: "100%",
              display: "block",
              p: spacing.Spacing.sm.$value,
            }}
          >
            <Typography
              sx={{
                fontSize: typography.Size.Text.xs.$value,
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              Run Range
              <Box sx={{ fontWeight: typography.Font_weight.Semibold.$value }}>
                {runRange}
              </Box>
            </Typography>
          </ListItem>
          <Divider />
          <ListItem
            sx={{
              width: "100%",
              display: "block",
              p: spacing.Spacing.sm.$value,
            }}
          >
            <Typography
              sx={{
                fontSize: typography.Size.Text.xs.$value,
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              Run By
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: spacing.Spacing.xs.$value,
                }}
              >
                {isSystem ? (
                  <Avatar
                    src={robot}
                    alt="System"
                    sx={{
                      width: 16,
                      height: 16,
                    }}
                  />
                ) : (
                  <Avatar
                    sx={{
                      width: 18,
                      height: 18,
                      fontSize: typography.Size.Text.xs.$value,
                      bgcolor: colors.Brand[500].$value,
                    }}
                  >
                    {firstLetter}
                  </Avatar>
                )}

                <Typography sx={{ fontSize: typography.Size.Text.xs.$value }}>
                  {runBy}
                </Typography>
              </Box>
            </Typography>
          </ListItem>
          <Divider />
          <ListItem
            sx={{
              width: "100%",
              display: "block",
              p: spacing.Spacing.sm.$value,
            }}
          >
            <Typography
              sx={{
                fontSize: typography.Size.Text.xs.$value,
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              Status
              <Box sx={{ fontWeight: typography.Font_weight.Semibold.$value }}>
                <Chip
                  label={status}
                  size="small"
                  sx={{
                    borderRadius: spacing.Radius.xs.$value,

                    bgcolor:
                      status?.toLowerCase() === "success"
                        ? colors.Success[50].$value
                        : colors.Error[50].$value,

                    color:
                      status?.toLowerCase() === "success"
                        ? colors.Success[600].$value
                        : colors.Error[600].$value,

                    fontWeight: typography.Font_weight.Semibold.$value,
                  }}
                />
              </Box>
            </Typography>
          </ListItem>
        </List>
      </Stack>
    </Box>
  );

  return (
    <>
      <IconButton
        color="secondary"
        size="small"
        sx={{ padding: 0 }}
        onClick={toggleDrawer(true)}
        data-tracker="656"
      >
        <InfoOutlinedIcon
          sx={{
            color: colors.GrayLightMode[700].$value,
            fontSize: typography.Size.Text.xl.$value,
          }}
        />
      </IconButton>

      <Drawer
        anchor="bottom"
        open={open}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: spacing.Radius.xl.$value,
            borderTopRightRadius: spacing.Radius.xl.$value,
            p: spacing.Spacing.xl.$value + "!important",
          },
        }}
      >
        {LastRunList}
      </Drawer>
    </>
  );
};

export default BrandsSearchItemsCardLastRun;
